/**
 * Order Queue column layout: one list of tracks and one grid-template-columns
 * string shared by the column header and every queue row, so cells line up.
 *
 * Every track is a fixed length or `minmax(<fixed>, 1fr)`, so a track's width
 * comes from the container width only, never from a cell's content: separate
 * per-row grids with the same template line up exactly. Never add `auto`,
 * `min-content` or `max-content` tracks.
 *
 * Density is chosen from the measured list width (the sidebar changes the real
 * width, so viewport breakpoints would lie), recomputed as the sidebar opens or
 * closes:
 * - wide: the Flags column holds the row's exception pills inline.
 * - compact: the Flags column becomes a small flag button (count + popover)
 *   right before the chat icon, and Customer flexes from 7rem.
 * - narrow: compact without Type and Items (both still show when a row is
 *   expanded). Below narrow the list scrolls sideways inside its card.
 *
 * Not CSS container queries: the cut-offs depend on two runtime inputs
 * (client columns, the Type toggle), and a pure function is easy to test.
 */

export type QueueColumnKey =
  | 'select'
  | 'expand'
  | 'order'
  | 'client'
  | 'customer'
  | 'type'
  | 'items'
  | 'status'
  | 'flags'
  | 'age'
  | 'flagsButton'
  | 'chat'
  | 'actions';

export type QueueDensity = 'wide' | 'compact' | 'narrow';

export interface QueueColumnOptions {
  /** The page shows client rows or has connected clients. */
  showClientColumns: boolean;
  /** Client columns show and the caller may open the Client messages inbox. */
  showChat: boolean;
  /** The "Fulfilment type" Display toggle. */
  showType: boolean;
}

export interface QueueColumnsLayout {
  density: QueueDensity;
  /** The tracks present, in order. Header and rows render one cell per entry. */
  columns: readonly QueueColumnKey[];
  /** One track per entry in `columns`; identical for the header and every row. */
  gridTemplateColumns: string;
  /** Column gap for this density (0.75rem wide, 0.5rem compact and narrow). */
  columnGap: string;
  /** Set below the narrow minimum: the list scrolls sideways inside its card. */
  minWidth: string | null;
}

const REM_PX = 16;

/**
 * Row chrome, ONE place: the horizontal padding and left ribbon every queue row
 * AND the column header use (QUEUE_ROW_CHROME_CLASS), and the px numbers the
 * tier cut-offs and the expanded-panel offset are computed from. Change the
 * class and the numbers together, here, so the cut-offs cannot drift from the CSS.
 */
/** Horizontal padding of a row; the expanded line-items panel uses it too. */
export const QUEUE_ROW_PADDING_CLASS = 'px-4';
export const QUEUE_ROW_CHROME_CLASS = `${QUEUE_ROW_PADDING_CLASS} border-l-4`;
/** px-4: horizontal padding on each side of a row. */
export const QUEUE_ROW_PADDING_PX = REM_PX;
/** border-l-4: the left ribbon. */
export const QUEUE_ROW_RIBBON_PX = 4;
/** Total horizontal chrome: padding on both sides + the ribbon. */
export const QUEUE_ROW_CHROME_PX = 2 * QUEUE_ROW_PADDING_PX + QUEUE_ROW_RIBBON_PX;

const GAP_REM: Record<QueueDensity, number> = { wide: 0.75, compact: 0.5, narrow: 0.5 };

/** Fixed track widths in rem (customer and flags are handled per density). */
const FIXED_REM = {
  select: 1.5,
  expand: 1,
  order: 6.5,
  client: 8,
  type: 5,
  items: 4.5,
  status: 8.5,
  age: 2.75,
  flagsButton: 1.75,
  chat: 1.75,
  actions: 1.75,
} as const;

/** Customer is fixed in wide and flexes from this minimum in compact / narrow. */
const CUSTOMER_WIDE_REM = 11;
const CUSTOMER_MIN_REM = 7;
/** Room the wide tier must leave the inline Flags column. */
const FLAGS_MIN_REM = 12;

function columnsFor(density: QueueDensity, opts: QueueColumnOptions): QueueColumnKey[] {
  const cols: QueueColumnKey[] = ['select', 'expand', 'order'];
  if (opts.showClientColumns) cols.push('client');
  cols.push('customer');
  if (density !== 'narrow') {
    if (opts.showType) cols.push('type');
    cols.push('items');
  }
  cols.push('status');
  if (density === 'wide') cols.push('flags');
  cols.push('age');
  if (density !== 'wide') cols.push('flagsButton');
  if (opts.showChat) cols.push('chat');
  cols.push('actions');
  return cols;
}

function trackRem(key: QueueColumnKey, density: QueueDensity): number {
  if (key === 'customer') return density === 'wide' ? CUSTOMER_WIDE_REM : CUSTOMER_MIN_REM;
  if (key === 'flags') return FLAGS_MIN_REM;
  return FIXED_REM[key];
}

function trackCss(key: QueueColumnKey, density: QueueDensity): string {
  if (key === 'customer') {
    return density === 'wide' ? `${CUSTOMER_WIDE_REM}rem` : `minmax(${CUSTOMER_MIN_REM}rem, 1fr)`;
  }
  if (key === 'flags') return 'minmax(0, 1fr)';
  return `${FIXED_REM[key]}rem`;
}

/** Minimum row CONTENT width (inside the row padding and ribbon) for a density. */
export function minQueueContentWidth(density: QueueDensity, opts: QueueColumnOptions): number {
  const cols = columnsFor(density, opts);
  const tracks = cols.reduce((sum, key) => sum + trackRem(key, density), 0);
  return tracks * REM_PX + (cols.length - 1) * GAP_REM[density] * REM_PX;
}

const comboIndex = (opts: QueueColumnOptions) =>
  (opts.showClientColumns ? 4 : 0) + (opts.showChat ? 2 : 0) + (opts.showType ? 1 : 0);

/** Precomputed once: [wide, compact, narrow] minimum CONTENT widths per option combination. */
const MINIMUMS: ReadonlyArray<readonly [number, number, number]> = Array.from({ length: 8 }, (_, i) => {
  const opts: QueueColumnOptions = {
    showClientColumns: (i & 4) !== 0,
    showChat: (i & 2) !== 0,
    showType: (i & 1) !== 0,
  };
  return [
    minQueueContentWidth('wide', opts),
    minQueueContentWidth('compact', opts),
    minQueueContentWidth('narrow', opts),
  ] as const;
});

/**
 * Pick the widest density that fits the measured list width. Cheap enough for
 * every ResizeObserver tick: a few comparisons against precomputed minimums.
 * `null` (not measured yet) gives compact.
 */
export function resolveQueueTier(
  listWidth: number | null,
  opts: QueueColumnOptions,
): { density: QueueDensity; belowNarrow: boolean } {
  if (listWidth == null) return { density: 'compact', belowNarrow: false };
  const content = listWidth - QUEUE_ROW_CHROME_PX;
  const [wide, compact, narrow] = MINIMUMS[comboIndex(opts)]!;
  if (content >= wide) return { density: 'wide', belowNarrow: false };
  if (content >= compact) return { density: 'compact', belowNarrow: false };
  return { density: 'narrow', belowNarrow: content < narrow };
}

export function buildQueueColumns(
  input: QueueColumnOptions & { density: QueueDensity; belowNarrow: boolean },
): QueueColumnsLayout {
  const { density, belowNarrow } = input;
  const columns = columnsFor(density, input);
  return {
    density,
    columns,
    gridTemplateColumns: columns.map((key) => trackCss(key, density)).join(' '),
    columnGap: `${GAP_REM[density]}rem`,
    minWidth: belowNarrow
      ? `${minQueueContentWidth('narrow', input) + QUEUE_ROW_CHROME_PX}px`
      : null,
  };
}

/**
 * Left edge of the Order column, in px from the row's outer left edge (ribbon
 * + padding + select + expand tracks + their two gaps). The expanded line-items
 * panel starts here.
 */
export function orderColumnOffsetPx(density: QueueDensity): number {
  return (
    QUEUE_ROW_RIBBON_PX +
    QUEUE_ROW_PADDING_PX +
    (FIXED_REM.select + FIXED_REM.expand + 2 * GAP_REM[density]) * REM_PX
  );
}

/** The row fallback: wide, with client, chat and type tracks. */
export const DEFAULT_QUEUE_LAYOUT: QueueColumnsLayout = buildQueueColumns({
  density: 'wide',
  belowNarrow: false,
  showClientColumns: true,
  showChat: true,
  showType: true,
});
